import React, { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps, ExecutionLogEntry, ExecutionStatusData } from '@flow-studio/flow-observer-core';
import { useDesignerModeStore, useWorkflowStore } from '@flow-studio/store';
import './DashboardTabContent.css';

export const DashboardTabContent: React.FC<FlowObserverTabContentProps> = ({
  tabId,
  loadState,
  error,
  onRetry,
}) => {
  // Read from execution-logs buffer since that's where the data is pushed
  const dataBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates['execution-logs']?.dataBuffer ?? []
  );

  // Also get status data from execution-status tab
  const statusData = useFlowObserverPanelStore(
    (s) => {
      const buffer = s.tabRuntimeStates['execution-status']?.dataBuffer ?? [];
      return (buffer as ExecutionStatusData[]).length > 0
        ? (buffer[buffer.length - 1] as ExecutionStatusData)
        : null;
    }
  );

  const logEntries = dataBuffer as ExecutionLogEntry[];

  // Per-canvas-node execution status, keyed by node id — de-duplicates loop iterations
  // down to one status per distinct workflow node (see ExecutionStatusTabContent for
  // the full rationale).
  const nodeExecutionStates = useDesignerModeStore((s) => s.nodeExecutionStates);

  // The real universe of canvas nodes for this workflow — bounds distinctNodeCounts below
  // (see ExecutionStatusTabContent for the full rationale: some node types, e.g. an AI
  // Agent's internal tool-call turns, emit events keyed by a synthetic per-turn id rather
  // than the canvas node's own id, which would otherwise inflate the distinct count).
  const canvasNodes = useWorkflowStore((s) => s.nodes);
  const canvasNodeIds = useMemo(() => new Set(canvasNodes.map((n) => n.id)), [canvasNodes]);
  const canvasEdges = useWorkflowStore((s) => s.edges);

  // Nodes on the execution path: every node reachable from a trigger through the canvas edges.
  // A node with no connection from a trigger is never executed, so it must not count toward the
  // total or the completed count. Falls back to the full canvas when no trigger is found.
  const executionPathNodeIds = useMemo(() => {
    const triggerIds = canvasNodes
      .filter((n) => n.data?.isTrigger || n.data?.processElement?.isTrigger)
      .map((n) => n.id);
    if (triggerIds.length === 0) return canvasNodeIds;

    const adjacency = new Map<string, string[]>();
    canvasEdges.forEach((edge) => {
      const next = adjacency.get(edge.source) ?? [];
      next.push(edge.target);
      adjacency.set(edge.source, next);
    });

    const reached = new Set<string>(triggerIds);
    const queue = [...triggerIds];
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const target of adjacency.get(current) ?? []) {
        if (!reached.has(target)) {
          reached.add(target);
          queue.push(target);
        }
      }
    }
    return reached;
  }, [canvasNodes, canvasEdges, canvasNodeIds]);

  const distinctNodeCounts = useMemo(() => {
    let success = 0;
    let failed = 0;
    nodeExecutionStates.forEach((state, nodeId) => {
      if (executionPathNodeIds.size > 0 && !executionPathNodeIds.has(nodeId)) return;
      const s = (state.status || '').toLowerCase();
      if (s === 'success') success++;
      else if (s === 'failed' || s === 'error' || s === 'timeout') failed++;
    });
    return { success, failed };
  }, [nodeExecutionStates, executionPathNodeIds]);

  // Calculate event rate over time
  const eventRateData = useMemo(() => {
    const entries = logEntries.slice(-60); // Last 60 events
    const data: Record<string, number> = {};

    entries.forEach((entry) => {
      const time = new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      data[time] = (data[time] || 0) + 1;
    });

    return Object.entries(data).map(([time, count]) => ({
      time,
      events: count,
    })).slice(-30);
  }, [logEntries]);

  // Calculate log level distribution
  const logLevelData = useMemo(() => {
    const levels = { debug: 0, info: 0, warn: 0, error: 0 };
    logEntries.forEach((entry) => {
      if (levels.hasOwnProperty(entry.level)) {
        levels[entry.level as keyof typeof levels]++;
      }
    });
    return Object.entries(levels)
      .filter(([, count]) => count > 0)
      .map(([level, count]) => ({
        name: level.charAt(0).toUpperCase() + level.slice(1),
        value: count,
        level,
      }));
  }, [logEntries]);

  // Calculate event type distribution
  const eventTypeData = useMemo(() => {
    const types: Record<string, number> = {};
    logEntries.forEach((entry) => {
      const type = entry.sourceNodeLabel || entry.level;
      types[type] = (types[type] || 0) + 1;
    });
    return Object.entries(types)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([name, value]) => ({ name: name.substring(0, 15), value }));
  }, [logEntries]);

  const entityCompletions = useMemo(() => {
    // Process/Thread completion has no dedicated per-entity signal from the backend, unlike
    // Node Completed (nodeExecutionStates via SignalR node-status events). Previously this scanned
    // the raw log buffer for entries whose rawDetail.item.entityType matched 'Process'/'ProcessThread'
    // and whose message text contained the substring "completed" — if the backend didn't emit a log
    // entry shaped exactly that way, the count stuck at 0 even though the run had clearly finished.
    // A single execution has exactly one Process and one root ProcessThread, so runState === 'completed'
    // (the same reliable field the node-completed fallback below already relies on) is a correct proxy.
    const threadCompleted = statusData?.runState === 'completed' ? 1 : 0;
    const processCompleted = statusData?.runState === 'completed' ? 1 : 0;

    // The backend's nodesCompleted counter counts every execution of a node, so it can
    // vastly exceed the node total for a Loop workflow — use the distinct per-node
    // completion count instead, bounded by the node total (see distinctNodeCounts above).
    // Prefer the real canvas node count over the backend-reported nodesTotal, which can be
    // inflated for node types (e.g. multi-step Form nodes) counted server-side as several
    // internal elements despite rendering as one canvas node (see ExecutionStatusTabContent).
    const nodesTotal = executionPathNodeIds.size > 0 ? executionPathNodeIds.size : (statusData?.nodesTotal ?? 0);
    const nodeCompleted =
      statusData?.runState === 'completed'
        ? Math.max(nodesTotal - Math.min(nodesTotal, distinctNodeCounts.failed), 0)
        : Math.min(nodesTotal, distinctNodeCounts.success);

    return {
      nodeCompleted,
      threadCompleted,
      processCompleted,
    };
  }, [statusData, distinctNodeCounts, executionPathNodeIds]);

  // Calculate log level over time (area chart)
  const logLevelTimeData = useMemo(() => {
    const entries = logEntries.slice(-30);
    interface LogLevelPoint {
      time: string;
      debug: number;
      info: number;
      warn: number;
      error: number;
    }
    const data: Record<string, LogLevelPoint> = {};

    entries.forEach((entry) => {
      const time = new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (!data[time]) {
        data[time] = { time, debug: 0, info: 0, warn: 0, error: 0 };
      }
      if (data[time].hasOwnProperty(entry.level)) {
        data[time][entry.level as keyof Omit<LogLevelPoint, 'time'>]++;
      }
    });

    return Object.values(data);
  }, [logEntries]);

  const metrics = useMemo(() => {
    if (!statusData) {
      return {
        nodesTotal: 0,
        nodesCompleted: 0,
        nodesFailed: 0,
        runState: 'idle',
        completionRate: 0,
        failureRate: 0,
        elapsedMs: 0,
        eventCount: logEntries.length,
        avgEventRate: 0,
      };
    }

    // Prefer the real canvas node count over the backend-reported nodesTotal — some node
    // types (e.g. a multi-step Form node) are counted server-side as several internal
    // elements even though they render as a single canvas node, which inflates nodesTotal
    // past the workflow's actual node count and, via the "fill remaining on clean finish"
    // rule below, forces Completed to match that inflated total (see ExecutionStatusTabContent
    // for the full rationale).
    const total = executionPathNodeIds.size > 0 ? executionPathNodeIds.size : (statusData.nodesTotal || 1);
    // Use distinct per-node status (bounded by `total`) rather than the backend's raw
    // nodesCompleted/nodesFailed counters — a Loop node re-executes the same canvas
    // nodes once per array item, so those counters can vastly exceed the node total.
    let completed = Math.min(total, distinctNodeCounts.success);
    let failed = Math.min(total - completed, distinctNodeCounts.failed);
    if (statusData.runState === 'completed' && completed + failed < total) {
      completed = total - failed;
    }

    return {
      nodesTotal: total,
      nodesCompleted: completed,
      nodesFailed: failed,
      runState: statusData.runState,
      completionRate: Math.min(100, Math.round((completed / total) * 100)),
      failureRate: total > 0 ? Math.min(100, Math.round((failed / total) * 100)) : 0,
      elapsedMs: statusData.elapsedMs || 0,
      eventCount: logEntries.length,
      avgEventRate: (statusData.elapsedMs ?? 0) > 0 ? Math.round((logEntries.length / (statusData.elapsedMs! / 1000)) * 100) / 100 : 0,
    };
  }, [statusData, logEntries, distinctNodeCounts, executionPathNodeIds]);

  if (loadState === 'loading') return <div className="fop-loading">Loading…</div>;

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  const COLORS = {
    debug: '#8884d8',
    info: '#82ca9d',
    warn: '#ffc658',
    error: '#ff7c7c',
  };

  return (
    <div className="fop-dashboard">
      {/* Top Metrics */}
      <div className="fop-dashboard__metrics">
        <div className="fop-dashboard__metric-card">
          <div className="fop-dashboard__metric-label">Status</div>
          <div className={`fop-dashboard__metric-value fop-dashboard__metric-value--${metrics.runState}`}>
            {metrics.runState.toUpperCase()}
          </div>
        </div>
        <div className="fop-dashboard__metric-card">
          <div className="fop-dashboard__metric-label">Total Events</div>
          <div className="fop-dashboard__metric-value">{metrics.eventCount}</div>
        </div>
        <div className="fop-dashboard__metric-card">
          <div className="fop-dashboard__metric-label">Event Rate</div>
          <div className="fop-dashboard__metric-value">{metrics.avgEventRate} evt/s</div>
        </div>
        <div className="fop-dashboard__metric-card">
          <div className="fop-dashboard__metric-label">Completion</div>
          <div className="fop-dashboard__metric-value">{metrics.completionRate}%</div>
        </div>
        <div className="fop-dashboard__metric-card">
          <div className="fop-dashboard__metric-label">Nodes Completed</div>
          <div className="fop-dashboard__metric-value">{metrics.nodesCompleted}/{metrics.nodesTotal}</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="fop-dashboard__charts">
        {/* Event Rate Over Time */}
        <div className="fop-dashboard__chart-container">
          <h3 className="fop-dashboard__chart-title">Event Rate Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={eventRateData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Line
                type="monotone"
                dataKey="events"
                stroke="#3b82f6"
                dot={false}
                strokeWidth={2}
                isAnimationActive={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Log Level Distribution Pie */}
        <div className="fop-dashboard__chart-container">
          <h3 className="fop-dashboard__chart-title">Log Level Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={logLevelData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={70}
                fill="#8884d8"
                dataKey="value"
                animationBegin={0}
                animationDuration={600}
              >
                {logLevelData.map((entry) => (
                  <Cell
                    key={`cell-${entry.level}`}
                    fill={COLORS[entry.level as keyof typeof COLORS]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                labelStyle={{ color: '#d1d5db' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Log Level Over Time Area Chart */}
        <div className="fop-dashboard__chart-container fop-dashboard__chart-container--full">
          <h3 className="fop-dashboard__chart-title">Log Level Timeline</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={logLevelTimeData} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="debug"
                stackId="1"
                stroke={COLORS.debug}
                fill={COLORS.debug}
                isAnimationActive={true}
              />
              <Area
                type="monotone"
                dataKey="info"
                stackId="1"
                stroke={COLORS.info}
                fill={COLORS.info}
                isAnimationActive={true}
              />
              <Area
                type="monotone"
                dataKey="warn"
                stackId="1"
                stroke={COLORS.warn}
                fill={COLORS.warn}
                isAnimationActive={true}
              />
              <Area
                type="monotone"
                dataKey="error"
                stackId="1"
                stroke={COLORS.error}
                fill={COLORS.error}
                isAnimationActive={true}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Top Event Sources */}
        <div className="fop-dashboard__chart-container fop-dashboard__chart-container--full">
          <h3 className="fop-dashboard__chart-title">Top Event Sources</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={eventTypeData} margin={{ top: 20, right: 30, left: 200, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis type="category" dataKey="name" stroke="#9ca3af" style={{ fontSize: '11px' }} width={190} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Bar dataKey="value" fill="#8b5cf6" isAnimationActive={true} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Entity Completions */}
        <div className="fop-dashboard__chart-container">
          <h3 className="fop-dashboard__chart-title">Entity Completions</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={[{
              name: 'Completions',
              Process: entityCompletions.processCompleted,
              Thread: entityCompletions.threadCompleted,
              Node: entityCompletions.nodeCompleted,
            }]} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Legend />
              <Bar dataKey="Process" fill="#10b981" />
              <Bar dataKey="Thread" fill="#3b82f6" />
              <Bar dataKey="Node" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Node Status */}
        {metrics.nodesTotal > 0 && (
          <div className="fop-dashboard__chart-container">
            <h3 className="fop-dashboard__chart-title">Node Execution Status</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={[{
                name: 'Nodes',
                Completed: metrics.nodesCompleted,
                Failed: metrics.nodesFailed,
                Remaining: Math.max(0, metrics.nodesTotal - metrics.nodesCompleted - metrics.nodesFailed),
              }]} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                  labelStyle={{ color: '#d1d5db' }}
                />
                <Legend />
                <Bar dataKey="Completed" stackId="a" fill="#10b981" />
                <Bar dataKey="Failed" stackId="a" fill="#ef4444" />
                <Bar dataKey="Remaining" stackId="a" fill="#6b7280" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
